import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Chip,
  TextField,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Add,
  Edit as EditIcon,
  Delete,
  ContentCopy,
  ContentPaste,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Close,
  Save,
  Undo,
  Error as ErrorIcon,
  WorkOutline,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";
import { Shop, CoverageRequirement } from "../../types/shop.types";
import { DayOfWeek } from "../../types/common.types";
import { useShops } from "../../hooks/useShops";
import { shopsService } from "../../services/firebase/shops.service";
import DateNavigator from "../common/DateNavigator";
import TimeRangeSelector from "../common/TimeRangeSelector";
import { startTimeToMinutes, endTimeToMinutes } from "../../utils/timeHelpers";

interface ShopCoverageCalendarProps {
  shops: Shop[];
  isLoading: boolean;
  canWrite: boolean;
  allowEditHistory?: boolean;
}

const dayOfWeekOrder: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const dayLabels: Record<DayOfWeek, string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

export default function ShopCoverageCalendar({
  shops,
  isLoading,
  canWrite,
  allowEditHistory = false,
}: ShopCoverageCalendarProps) {
  const { enqueueSnackbar } = useSnackbar();

  // Check if a date is in the past (before today)
  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Check if editing is allowed for a specific date
  const canEditDate = (date: Date): boolean => {
    if (!canWrite) return false;
    if (allowEditHistory) return true;
    return !isPastDate(date);
  };
  const { batchUpdateShopCoverage } = useShops();

  // Week navigation state (Monday-based)
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = day === 0 ? -6 : -(day - 1);
    const start = new Date(today);
    start.setDate(today.getDate() + diff);
    start.setHours(0, 0, 0, 0);
    return start;
  });

  // Dialog states
  const [coverageDialogOpen, setShiftDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedCoverage, setSelectedShift] =
    useState<CoverageRequirement | null>(null);
  const [coverageForm, setShiftForm] = useState({
    startTime: "09:00",
    endTime: "17:00",
    minCoverage: 1,
  });

  // Copy/Paste state
  const [copiedDayData, setCopiedDayData] = useState<{
    shopId: string;
    shopName: string;
    date: Date;
    coverage: CoverageRequirement[];
  } | null>(null);

  const [copiedWeekData, setCopiedWeekData] = useState<{
    weekStart: Date;
    shopShifts: Record<string, Record<string, CoverageRequirement[]>>;
  } | null>(null);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Local coverage state for unsaved changes
  // Key format: "shopId_dateKey" -> CoverageRequirement[]
  const [coverageState, setCoverageState] = useState<
    Record<string, CoverageRequirement[]>
  >({});
  const [originalCoverageState, setOriginalCoverageState] = useState<
    Record<string, CoverageRequirement[]>
  >({});

  // Error state for validation
  // Key format: "shopId_dateKey_coverageId" -> error message
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Unsaved changes context
  const { setHasUnsavedChanges } = useUnsavedChanges();

  // Helper to get ISO week number
  const getWeekNumber = (date: Date): number => {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  };

  // Helper to format date range
  const formatDateRange = (startDate: Date, endDate: Date): string => {
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const startMonth = startDate.toLocaleDateString("en-US", {
      month: "short",
    });
    const endMonth = endDate.toLocaleDateString("en-US", { month: "short" });
    const year = endDate.getFullYear();
    if (startMonth === endMonth) {
      return `${startDay}-${endDay} ${startMonth} ${year}`;
    }
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
  };

  // Clear all clipboard data
  const clearAllClipboard = () => {
    setCopiedDayData(null);
    setCopiedWeekData(null);
  };

  // Get dates for the current week
  const weekDates = useMemo(() => {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [weekStart]);

  // Helper to get date string in YYYY-MM-DD format (using local timezone)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Helper to create state key
  const getStateKey = (shopId: string, date: Date): string => {
    return `${shopId}_${formatDate(date)}`;
  };

  // Initialize coverage state from shops data
  useEffect(() => {
    const newCoverageState: Record<string, CoverageRequirement[]> = {};
    shops.forEach((shop) => {
      weekDates.forEach((date) => {
        const key = getStateKey(shop.id, date);
        const coverage = shopsService.getCoverageForDate(shop, date);
        newCoverageState[key] = [...coverage];
      });
    });
    setCoverageState(newCoverageState);
    setOriginalCoverageState(JSON.parse(JSON.stringify(newCoverageState)));
    setValidationErrors({});
  }, [shops, weekStart]);

  // Check if coverage completely fills the shop hours (no gaps)
  const isCoverageComplete = (
    shop: Shop,
    date: Date,
    coverage: CoverageRequirement[]
  ): boolean => {
    const day = getDayOfWeek(date);
    const dayHours = shop.defaultHours[day];

    if (!dayHours.isOpen || coverage.length === 0) {
      return false;
    }

    const shopOpen = startTimeToMinutes(dayHours.openTime);
    const shopClose = endTimeToMinutes(dayHours.closeTime);

    // Sort coverage by start time
    const sortedCoverage = [...coverage].sort(
      (a, b) =>
        startTimeToMinutes(a.startTime) - startTimeToMinutes(b.startTime)
    );

    // Check if first coverage starts at or before shop open
    const firstStart = startTimeToMinutes(sortedCoverage[0].startTime);
    if (firstStart > shopOpen) {
      return false; // Gap at the beginning
    }

    // Check for gaps between coverage slots
    let coveredUntil = endTimeToMinutes(sortedCoverage[0].endTime);
    for (let i = 1; i < sortedCoverage.length; i++) {
      const slotStart = startTimeToMinutes(sortedCoverage[i].startTime);
      const slotEnd = endTimeToMinutes(sortedCoverage[i].endTime);

      if (slotStart > coveredUntil) {
        return false; // Gap between slots
      }

      // Extend covered time (handle overlapping slots)
      coveredUntil = Math.max(coveredUntil, slotEnd);
    }

    // Check if coverage extends to shop close
    return coveredUntil >= shopClose;
  };

  // Validate a single coverage requirement against shop hours
  const validateCoverageTime = (
    shop: Shop,
    date: Date,
    coverage: CoverageRequirement
  ): string | null => {
    const day = getDayOfWeek(date);
    const dayHours = shop.defaultHours[day];

    if (!dayHours.isOpen) {
      return "Shop is closed on this day";
    }

    const coverageStart = startTimeToMinutes(coverage.startTime);
    const coverageEnd = endTimeToMinutes(coverage.endTime);
    const shopOpen = startTimeToMinutes(dayHours.openTime);
    const shopClose = endTimeToMinutes(dayHours.closeTime);

    if (coverageStart < shopOpen || coverageEnd > shopClose) {
      return `Outside shop hours (${dayHours.openTime}-${dayHours.closeTime})`;
    }

    if (coverageStart >= coverageEnd) {
      return "End time must be after start time";
    }

    return null;
  };

  // Validate all coverage and update error state
  const validateAllCoverage = useCallback(() => {
    const errors: Record<string, string> = {};

    shops.forEach((shop) => {
      weekDates.forEach((date) => {
        const key = getStateKey(shop.id, date);
        const coverageList = coverageState[key] || [];

        coverageList.forEach((coverage) => {
          const error = validateCoverageTime(shop, date, coverage);
          if (error) {
            errors[`${key}_${coverage.id}`] = error;
          }
        });
      });
    });

    setValidationErrors(errors);
  }, [coverageState, shops, weekDates]);

  // Run validation when coverage changes
  useEffect(() => {
    validateAllCoverage();
  }, [validateAllCoverage]);

  // Helper to compare two coverage arrays by content (ignoring IDs)
  const areCoverageArraysEqual = (
    arr1: CoverageRequirement[],
    arr2: CoverageRequirement[]
  ): boolean => {
    if (arr1.length !== arr2.length) return false;

    // Sort both arrays by startTime, endTime, minCoverage for comparison
    const normalize = (arr: CoverageRequirement[]) =>
      [...arr]
        .map((c) => ({
          startTime: c.startTime,
          endTime: c.endTime,
          minCoverage: c.minCoverage,
        }))
        .sort((a, b) => {
          if (a.startTime !== b.startTime)
            return a.startTime.localeCompare(b.startTime);
          if (a.endTime !== b.endTime)
            return a.endTime.localeCompare(b.endTime);
          return a.minCoverage - b.minCoverage;
        });

    const normalized1 = normalize(arr1);
    const normalized2 = normalize(arr2);

    return normalized1.every(
      (item, index) =>
        item.startTime === normalized2[index].startTime &&
        item.endTime === normalized2[index].endTime &&
        item.minCoverage === normalized2[index].minCoverage
    );
  };

  // Check if there are any unsaved changes
  const hasChanges = useCallback((): boolean => {
    const currentKeys = Object.keys(coverageState);
    const originalKeys = Object.keys(originalCoverageState);

    const allKeys = new Set([...currentKeys, ...originalKeys]);

    for (const key of allKeys) {
      const current = coverageState[key] || [];
      const original = originalCoverageState[key] || [];

      if (!areCoverageArraysEqual(current, original)) {
        return true;
      }
    }
    return false;
  }, [coverageState, originalCoverageState]);

  // Get count of changes
  const getChangesCount = (): number => {
    let count = 0;
    const allKeys = new Set([
      ...Object.keys(coverageState),
      ...Object.keys(originalCoverageState),
    ]);

    for (const key of allKeys) {
      const current = coverageState[key] || [];
      const original = originalCoverageState[key] || [];

      if (!areCoverageArraysEqual(current, original)) {
        count++;
      }
    }
    return count;
  };

  // Check if there are validation errors
  const hasValidationErrors = (): boolean => {
    return Object.keys(validationErrors).length > 0;
  };

  // Get count of validation errors
  const getValidationErrorsCount = (): number => {
    return Object.keys(validationErrors).length;
  };

  // Check if a specific cell has changes
  const cellHasChanges = (shopId: string, date: Date): boolean => {
    const key = getStateKey(shopId, date);
    const current = coverageState[key] || [];
    const original = originalCoverageState[key] || [];
    return !areCoverageArraysEqual(current, original);
  };

  // Get errors for a specific cell
  const getCellErrors = (shopId: string, date: Date): string[] => {
    const key = getStateKey(shopId, date);
    const errors: string[] = [];

    Object.entries(validationErrors).forEach(([errorKey, errorMsg]) => {
      if (errorKey.startsWith(key)) {
        errors.push(errorMsg);
      }
    });

    return errors;
  };

  // Check if a shop has changes
  const shopHasChanges = (shopId: string): boolean => {
    for (const date of weekDates) {
      if (cellHasChanges(shopId, date)) return true;
    }
    return false;
  };

  // Get count of changes for a shop
  const getShopChangesCount = (shopId: string): number => {
    let count = 0;
    for (const date of weekDates) {
      if (cellHasChanges(shopId, date)) count++;
    }
    return count;
  };

  // Sync unsaved changes state with context
  useEffect(() => {
    setHasUnsavedChanges(hasChanges());
  }, [hasChanges, setHasUnsavedChanges]);

  // Warn user when trying to leave with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  // Revert all changes
  const handleRevertChanges = () => {
    setCoverageState(JSON.parse(JSON.stringify(originalCoverageState)));
    enqueueSnackbar("Changes reverted", { variant: "info" });
  };

  // Revert changes for a specific shop (available for future use)
  const _handleRevertShop = (shopId: string) => {
    setCoverageState((prev) => {
      const newState = { ...prev };
      weekDates.forEach((date) => {
        const key = getStateKey(shopId, date);
        newState[key] = JSON.parse(
          JSON.stringify(originalCoverageState[key] || [])
        );
      });
      return newState;
    });
  };
  void _handleRevertShop; // Suppress unused warning

  // Revert changes for a specific cell (back to what was loaded) - available for future use
  const _handleRevertCell = (shopId: string, date: Date) => {
    const key = getStateKey(shopId, date);
    setCoverageState((prev) => ({
      ...prev,
      [key]: JSON.parse(JSON.stringify(originalCoverageState[key] || [])),
    }));
  };
  void _handleRevertCell; // Suppress unused warning

  // Revert to shop's default coverage template for this day
  const handleRevertToShopDefault = (shop: Shop, date: Date) => {
    const day = getDayOfWeek(date);
    const defaultCoverage = shop.defaultCoverage?.[day] || [];

    // Create new coverage items with new IDs from the defaults
    const newCoverage = defaultCoverage.map((coverage) => ({
      ...coverage,
      id: `coverage_${Date.now()}_${Math.random().toString(36).substring(7)}`,
    }));

    updateLocalCoverage(shop.id, date, newCoverage);
  };

  // Save all changes in bulk (one API call per shop)
  const handleSaveAllChanges = async () => {
    if (hasValidationErrors()) {
      enqueueSnackbar("Fix validation errors before saving", {
        variant: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Collect batch updates per shop
      const shopUpdates: Map<
        string,
        Record<string, CoverageRequirement[] | "delete">
      > = new Map();

      // Process each shop and date
      for (const shop of shops) {
        const updates: Record<string, CoverageRequirement[] | "delete"> = {};

        for (const date of weekDates) {
          const key = getStateKey(shop.id, date);
          const current = coverageState[key] || [];
          const day = getDayOfWeek(date);
          const dateKey = formatDate(date);
          const defaultCoverage = shop.defaultCoverage?.[day] || [];

          // Check if DB has a date-specific entry
          const hasDbEntry = !!(
            shop.dateCoverage && shop.dateCoverage[dateKey]
          );

          // Check if current matches default
          const matchesDefault = areCoverageArraysEqual(
            current,
            defaultCoverage
          );

          if (matchesDefault && hasDbEntry) {
            // Current matches default but DB has custom entry → delete it
            updates[dateKey] = "delete";
          } else if (!matchesDefault) {
            // Current differs from default → should have date-specific entry
            // Check if we need to save (content differs from what's in DB)
            const dbCoverage = hasDbEntry
              ? shop.dateCoverage![dateKey]
              : defaultCoverage;

            if (!areCoverageArraysEqual(current, dbCoverage)) {
              updates[dateKey] = current;
            }
          }
          // If matchesDefault && !hasDbEntry → nothing to do (already using default)
        }

        // Only add shop to batch if it has updates
        if (Object.keys(updates).length > 0) {
          shopUpdates.set(shop.id, updates);
        }
      }

      // Execute batch updates in parallel (one call per shop)
      if (shopUpdates.size > 0) {
        const savePromises: Promise<void>[] = [];
        for (const [shopId, updates] of shopUpdates) {
          savePromises.push(batchUpdateShopCoverage(shopId, updates));
        }
        await Promise.all(savePromises);
      }

      setOriginalCoverageState(JSON.parse(JSON.stringify(coverageState)));
      enqueueSnackbar("All changes saved successfully!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to save some changes", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Get local coverage for a cell (from local state, with fallback to shop data)
  const getLocalCoverage = (
    shopId: string,
    date: Date
  ): CoverageRequirement[] => {
    const key = getStateKey(shopId, date);
    // If we have local state for this key, use it
    if (key in coverageState) {
      return coverageState[key];
    }
    // Otherwise fall back to shop's actual data (prevents flash during week transitions)
    const shop = shops.find((s) => s.id === shopId);
    if (shop) {
      return shopsService.getCoverageForDate(shop, date);
    }
    return [];
  };

  // Update local coverage state
  const updateLocalCoverage = (
    shopId: string,
    date: Date,
    coverage: CoverageRequirement[]
  ) => {
    const key = getStateKey(shopId, date);
    setCoverageState((prev) => ({
      ...prev,
      [key]: coverage,
    }));
  };

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Handle date change from DateNavigator (receives YYYY-MM-DD string)
  const handleDateChange = (dateStr: string) => {
    const date = new Date(dateStr + "T00:00:00");
    const day = date.getDay();
    const diff = day === 0 ? -6 : -(day - 1);
    const start = new Date(date);
    start.setDate(date.getDate() + diff);
    start.setHours(0, 0, 0, 0);
    setWeekStart(start);
  };

  // Get selected date string for DateNavigator (use weekStart)
  const selectedDateStr = useMemo(() => formatDate(weekStart), [weekStart]);

  // Helper to get day of week from date
  const getDayOfWeek = (date: Date): DayOfWeek => {
    const days: DayOfWeek[] = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    return days[date.getDay()];
  };

  // Coverage dialog handlers
  const handleAddShift = (shop: Shop, date: Date) => {
    if (!canEditDate(date)) return;

    const day = getDayOfWeek(date);
    const shopOpenTime = shop.defaultHours[day].openTime;
    const shopCloseTime = shop.defaultHours[day].closeTime;

    // Get existing coverage for this day
    const existingCoverage = getLocalCoverage(shop.id, date);

    // Find the latest end time among existing coverage
    let startTime = shopOpenTime;
    if (existingCoverage.length > 0) {
      // Find the maximum end time using endTimeToMinutes for proper overnight handling
      let latestEndMinutes = 0;
      let latestEndTime = shopOpenTime;

      existingCoverage.forEach((coverage) => {
        const endMinutes = endTimeToMinutes(coverage.endTime);
        if (endMinutes > latestEndMinutes) {
          latestEndMinutes = endMinutes;
          latestEndTime = coverage.endTime;
        }
      });

      // Only use the latest end time if it's before shop close time
      const shopCloseMinutes = endTimeToMinutes(shopCloseTime);
      if (latestEndMinutes < shopCloseMinutes) {
        startTime = latestEndTime;
      }
    }

    setSelectedShop(shop);
    setSelectedDate(date);
    setSelectedShift(null);
    setShiftForm({
      startTime,
      endTime: shopCloseTime,
      minCoverage: 1,
    });
    setShiftDialogOpen(true);
  };

  const handleEditShift = (
    shop: Shop,
    date: Date,
    coverage: CoverageRequirement,
    _isDefault: boolean
  ) => {
    if (!canEditDate(date)) return;

    setSelectedShop(shop);
    setSelectedDate(date);
    setSelectedShift(coverage);
    setShiftForm({
      startTime: coverage.startTime,
      endTime: coverage.endTime,
      minCoverage: coverage.minCoverage,
    });
    setShiftDialogOpen(true);
  };

  const handleDeleteShift = (
    shop: Shop,
    date: Date,
    coverage: CoverageRequirement,
    _isDefault: boolean
  ) => {
    if (!canEditDate(date)) return;

    // Get current local coverage and filter out the deleted item
    const currentCoverage = getLocalCoverage(shop.id, date);
    const newCoverage = currentCoverage.filter((c) => c.id !== coverage.id);

    // Update local state
    updateLocalCoverage(shop.id, date, newCoverage);
  };

  const handleSaveShift = () => {
    if (!selectedShop || !selectedDate) return;

    // Get current local coverage
    const currentCoverage = getLocalCoverage(selectedShop.id, selectedDate);

    if (selectedCoverage) {
      // Editing existing coverage - update the item in the array
      const newCoverage = currentCoverage.map((c) =>
        c.id === selectedCoverage.id ? { ...c, ...coverageForm } : c
      );
      updateLocalCoverage(selectedShop.id, selectedDate, newCoverage);
    } else {
      // Creating new coverage
      const newCoverageItem: CoverageRequirement = {
        id: `coverage_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        ...coverageForm,
      };
      updateLocalCoverage(selectedShop.id, selectedDate, [
        ...currentCoverage,
        newCoverageItem,
      ]);
    }

    setShiftDialogOpen(false);
  };

  // Set default coverage for a specific date
  const handleSetDefaultShifts = (shop: Shop, date: Date) => {
    if (!canEditDate(date)) return;

    const day = getDayOfWeek(date);
    const defaultCoverage = shop.defaultCoverage?.[day] || [];

    if (defaultCoverage.length === 0) {
      enqueueSnackbar("No default coverage configured for this day", {
        variant: "warning",
      });
      return;
    }

    // Create new coverage with new IDs from defaults
    const newShifts = defaultCoverage.map((coverage) => ({
      ...coverage,
      id: `coverage_${Date.now()}_${Math.random().toString(36).substring(7)}`,
    }));

    updateLocalCoverage(shop.id, date, newShifts);
  };

  // Copy day's coverage
  const handleCopyDay = (shop: Shop, date: Date) => {
    const coverage = getLocalCoverage(shop.id, date);

    if (coverage.length === 0) {
      enqueueSnackbar("No coverage to copy", { variant: "warning" });
      return;
    }

    setCopiedDayData({
      shopId: shop.id,
      shopName: shop.name,
      date: new Date(date),
      coverage: [...coverage],
    });

    setCopiedWeekData(null); // Clear week copy
    enqueueSnackbar(`Copied ${coverage.length} coverage(s) from ${shop.name}`, {
      variant: "info",
    });
  };

  // Copy entire week's coverage for all shops
  const handleCopyWeek = () => {
    const shopShifts: Record<
      string,
      Record<string, CoverageRequirement[]>
    > = {};

    shops.forEach((shop) => {
      const shopDates: Record<string, CoverageRequirement[]> = {};
      weekDates.forEach((date) => {
        const dateKey = formatDate(date);
        const coverage = getLocalCoverage(shop.id, date);
        if (coverage.length > 0) {
          shopDates[dateKey] = [...coverage];
        }
      });
      if (Object.keys(shopDates).length > 0) {
        shopShifts[shop.id] = shopDates;
      }
    });

    if (Object.keys(shopShifts).length === 0) {
      enqueueSnackbar("No coverage to copy", { variant: "warning" });
      return;
    }

    setCopiedWeekData({
      weekStart: new Date(weekStart),
      shopShifts,
    });

    setCopiedDayData(null); // Clear day copy
    enqueueSnackbar("Copied entire week's coverage", { variant: "info" });
  };

  // Paste day's coverage
  const handlePasteDay = (targetShop: Shop, targetDate: Date) => {
    if (!copiedDayData) return;

    // Create new coverage with new IDs
    const newShifts = copiedDayData.coverage.map((coverage) => ({
      ...coverage,
      id: `coverage_${Date.now()}_${Math.random().toString(36).substring(7)}`,
    }));

    updateLocalCoverage(targetShop.id, targetDate, newShifts);
    enqueueSnackbar(
      `Pasted ${newShifts.length} coverage(s) to ${targetShop.name}`,
      { variant: "info" }
    );
  };

  // Paste entire week's coverage
  const handlePasteWeek = () => {
    if (!copiedWeekData) return;

    const sourceWeekStart = copiedWeekData.weekStart;
    const targetWeekStart = weekStart;

    // Calculate day offset
    const dayOffset = Math.floor(
      (targetWeekStart.getTime() - sourceWeekStart.getTime()) /
        (1000 * 60 * 60 * 24)
    );

    let pastedCount = 0;
    const newCoverageState: Record<string, CoverageRequirement[]> = {};

    // Paste coverage for each shop
    for (const [shopId, dates] of Object.entries(copiedWeekData.shopShifts)) {
      const shop = shops.find((s) => s.id === shopId);
      if (!shop) continue;

      for (const [sourceDateKey, coverage] of Object.entries(dates)) {
        const sourceDate = new Date(sourceDateKey);
        const targetDate = new Date(sourceDate);
        targetDate.setDate(sourceDate.getDate() + dayOffset);

        // Create new coverage with new IDs
        const newShifts = coverage.map((c) => ({
          ...c,
          id: `coverage_${Date.now()}_${Math.random()
            .toString(36)
            .substring(7)}`,
        }));

        const key = getStateKey(shop.id, targetDate);
        newCoverageState[key] = newShifts;
        pastedCount += newShifts.length;
      }
    }

    // Batch update all coverage
    setCoverageState((prev) => ({
      ...prev,
      ...newCoverageState,
    }));

    enqueueSnackbar(`Pasted ${pastedCount} coverage(s)`, {
      variant: "info",
    });
  };
  // Render coverage cell
  const renderCoverageCell = (shop: Shop, dayIndex: number) => {
    const day = dayOfWeekOrder[dayIndex];
    const date = weekDates[dayIndex];
    const dayHours = shop.defaultHours[day];
    const dateKey = formatDate(date);

    // Get coverage from local state
    const coverage = getLocalCoverage(shop.id, date);

    // Get the shop's default coverage for this day
    const defaultCoverage = shop.defaultCoverage?.[day] || [];

    // Check if current coverage matches default (by content, ignoring IDs)
    // If it matches, we consider it as "using default"
    const isUsingDefault = areCoverageArraysEqual(coverage, defaultCoverage);

    // Check if explicitly empty (closed)
    const isExplicitlyClosed = coverage.length === 0;

    // Get errors and changes for this cell
    const cellErrors = getCellErrors(shop.id, date);
    const hasCellChanges = cellHasChanges(shop.id, date);

    // Check if there's room for more coverage
    const canAddMore = !isCoverageComplete(shop, date, coverage);

    return (
      <CoverageCell
        key={`${shop.id}-${dateKey}`}
        date={date}
        shop={shop}
        dayHours={dayHours}
        coverage={coverage}
        isUsingDefault={isUsingDefault}
        isExplicitlyClosed={isExplicitlyClosed}
        canWrite={canEditDate(date)}
        canAddMore={canAddMore}
        onEdit={handleEditShift}
        onDelete={handleDeleteShift}
        onAddShift={handleAddShift}
        onSetDefault={handleSetDefaultShifts}
        onRevertToDefault={() => handleRevertToShopDefault(shop, date)}
        onCopyDay={handleCopyDay}
        onPasteDay={handlePasteDay}
        hasCopiedData={!!copiedDayData}
        errors={cellErrors}
        hasChanges={hasCellChanges}
        validationErrors={validationErrors}
      />
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (shops.length === 0) {
    return (
      <Paper sx={{ p: 8, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No shops found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add shops to start configuring coverage templates
        </Typography>
      </Paper>
    );
  }

  const changesCount = getChangesCount();

  // Render minimized sidebar
  const renderMinimizedSidebar = () => (
    <Paper
      sx={{
        position: "fixed",
        left: 0,
        top: 64,
        bottom: 0,
        width: 64,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 2,
        zIndex: 10,
        borderRadius: 0,
      }}
    >
      <Tooltip title="Expand Panel" placement="right">
        <IconButton onClick={() => setSidebarOpen(true)} sx={{ mb: 2 }}>
          <MenuIcon />
        </IconButton>
      </Tooltip>

      <Divider sx={{ width: "100%", my: 1 }} />

      {canWrite && (
        <>
          <Tooltip
            title={hasValidationErrors() ? "Fix errors first" : "Save Changes"}
            placement="right"
          >
            <span>
              <IconButton
                onClick={handleSaveAllChanges}
                disabled={!hasChanges() || isSaving || hasValidationErrors()}
                color={
                  hasValidationErrors()
                    ? "error"
                    : hasChanges()
                    ? "primary"
                    : "default"
                }
                sx={{ mb: 1 }}
              >
                <Save />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Revert Changes" placement="right">
            <span>
              <IconButton
                onClick={handleRevertChanges}
                disabled={!hasChanges()}
                color={hasChanges() ? "warning" : "default"}
                sx={{ mb: 1 }}
              >
                <Undo />
              </IconButton>
            </span>
          </Tooltip>

          {hasValidationErrors() ? (
            <Chip
              label={getValidationErrorsCount()}
              size="small"
              color="error"
              sx={{ mt: 1, minWidth: 24, height: 24 }}
            />
          ) : hasChanges() ? (
            <Chip
              label={changesCount}
              size="small"
              color="primary"
              sx={{ mt: 1, minWidth: 24, height: 24 }}
            />
          ) : null}

          <Divider sx={{ width: "100%", my: 1 }} />

          <Tooltip title="Copy Week" placement="right">
            <IconButton onClick={handleCopyWeek} sx={{ mb: 1 }}>
              <ContentCopy />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={copiedWeekData ? "Paste Week" : "Nothing to paste"}
            placement="right"
          >
            <span>
              <IconButton
                onClick={handlePasteWeek}
                disabled={!copiedWeekData}
                color={copiedWeekData ? "primary" : "default"}
              >
                <ContentPaste />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </Paper>
  );

  // Render expanded sidebar
  const renderExpandedSidebar = () => (
    <Paper
      sx={{
        position: "fixed",
        left: 0,
        top: 64,
        bottom: 0,
        width: 280,
        p: 2,
        overflow: "auto",
        zIndex: 10,
        borderRadius: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Actions
        </Typography>
        <IconButton size="small" onClick={() => setSidebarOpen(false)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {canWrite && (
        <>
          {/* Save/Revert Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Changes
            </Typography>
            {hasValidationErrors() ? (
              <Chip
                label={`${getValidationErrorsCount()} error${
                  getValidationErrorsCount() !== 1 ? "s" : ""
                }`}
                size="small"
                color="error"
              />
            ) : hasChanges() ? (
              <Chip
                label={`${changesCount} unsaved`}
                size="small"
                color="warning"
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                No changes
              </Typography>
            )}
          </Box>

          <Tooltip
            title={
              hasValidationErrors() ? "Fix validation errors before saving" : ""
            }
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <Save />}
                onClick={handleSaveAllChanges}
                disabled={!hasChanges() || isSaving || hasValidationErrors()}
                fullWidth
                sx={{ mb: 1 }}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<Undo />}
            onClick={handleRevertChanges}
            disabled={!hasChanges()}
            fullWidth
            color="warning"
            sx={{ mb: 3 }}
          >
            Revert Changes
          </Button>

          <Divider sx={{ mb: 2 }} />

          {/* Copy/Paste Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Copy / Paste
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopy />}
              onClick={handleCopyWeek}
              fullWidth
            >
              Copy Week
            </Button>
            <Tooltip
              title={copiedWeekData ? "" : "Nothing copied"}
              placement="top"
            >
              <span style={{ width: "100%" }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ContentPaste />}
                  onClick={handlePasteWeek}
                  fullWidth
                  color={copiedWeekData ? "info" : "inherit"}
                  disabled={!copiedWeekData}
                >
                  Paste Week
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Clipboard Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Clipboard
            </Typography>
            {(copiedDayData || copiedWeekData) && (
              <Tooltip title="Clear clipboard">
                <IconButton size="small" onClick={clearAllClipboard}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {copiedWeekData ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "info.50",
                border: "1px solid",
                borderColor: "info.main",
                mb: 2,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <ContentCopy fontSize="small" color="info" />
                <Typography variant="body2" fontWeight={500}>
                  Entire Week Copied
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {formatDateRange(weekDates[0], weekDates[6])} (Week{" "}
                {getWeekNumber(weekDates[0])})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Object.keys(copiedWeekData.shopShifts).length} shop(s)
              </Typography>
            </Box>
          ) : copiedDayData ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "info.50",
                border: "1px solid",
                borderColor: "info.main",
                mb: 2,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <ContentCopy fontSize="small" color="info" />
                <Typography variant="body2" fontWeight={500}>
                  Day Coverage Copied
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {copiedDayData.shopName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {copiedDayData.coverage.length} coverage(s)
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.disabled"
              fontStyle="italic"
              sx={{ mb: 2 }}
            >
              Use copy buttons on cells to copy coverage
            </Typography>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Modified Shops List */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Modified Shops
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {!hasChanges() ? (
              <Typography
                variant="body2"
                color="text.disabled"
                fontStyle="italic"
              >
                No pending changes
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {shops
                  .filter((shop) => shopHasChanges(shop.id))
                  .map((shop) => {
                    const shopErrors = weekDates.some(
                      (date) => getCellErrors(shop.id, date).length > 0
                    );
                    return (
                      <Box
                        key={shop.id}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: shopErrors ? "error.50" : "warning.50",
                          border: "1px solid",
                          borderColor: shopErrors
                            ? "error.light"
                            : "warning.light",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: shop.color,
                              }}
                            />
                            <Typography variant="caption" fontWeight={500}>
                              {shop.name}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {shopErrors && (
                              <ErrorIcon
                                fontSize="small"
                                color="error"
                                sx={{ fontSize: 14 }}
                              />
                            )}
                            <Chip
                              label={`${getShopChangesCount(shop.id)} change${
                                getShopChangesCount(shop.id) !== 1 ? "s" : ""
                              }`}
                              size="small"
                              color={shopErrors ? "error" : "warning"}
                              sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Help Text */}
      <Box sx={{ mt: "auto", pt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="text.secondary">
          {canWrite
            ? "Configure coverage requirements for each shop. Use copy/paste to quickly replicate coverage between days and weeks."
            : "View-only mode. Contact an admin to edit coverage."}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <>
      {/* Sidebar */}
      {sidebarOpen ? renderExpandedSidebar() : renderMinimizedSidebar()}

      {/* Main content with margin for fixed sidebar */}
      <Box sx={{ ml: sidebarOpen ? "280px" : "64px" }}>
        {/* Week Navigator */}
        <DateNavigator
          mode="weekly"
          selectedDate={selectedDateStr}
          onDateChange={handleDateChange}
        />

        {/* Calendar Table */}
        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    width: 200,
                    position: "sticky",
                    left: 0,
                    bgcolor: "background.paper",
                    zIndex: 3,
                  }}
                >
                  Shop
                </TableCell>
                {weekDates.map((date, index) => {
                  const day = dayOfWeekOrder[index];
                  const isTodayCell = isToday(date);
                  return (
                    <TableCell
                      key={day}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={isTodayCell ? "primary.main" : "text.primary"}
                      >
                        {dayLabels[day]}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        color={isTodayCell ? "primary.main" : "text.primary"}
                      >
                        {date.getDate()}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {shops.map((shop) => (
                <TableRow key={shop.id} hover>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      bgcolor: "background.paper",
                      zIndex: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: shop.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {shop.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) =>
                    renderCoverageCell(shop, dayIndex)
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Helper text */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            {canWrite
              ? "Click 'Pencil' icon to edit default coverage • Copy/Paste coverage between days and weeks"
              : "View-only mode"}
          </Typography>
        </Box>
      </Box>

      {/* Coverage Dialog */}
      <Dialog
        open={coverageDialogOpen}
        onClose={() => setShiftDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <WorkOutline color="primary" />
            <Typography variant="h5" fontWeight={600}>
              {selectedCoverage ? "Edit Coverage" : "Add Coverage"}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {selectedShop?.name}
            {selectedDate && selectedShop && (
              <>
                {" "}
                •{" "}
                {selectedDate.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}{" "}
                • Shop hours:{" "}
                {selectedShop.defaultHours[getDayOfWeek(selectedDate)].openTime}{" "}
                -{" "}
                {
                  selectedShop.defaultHours[getDayOfWeek(selectedDate)]
                    .closeTime
                }
              </>
            )}
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {selectedShop && selectedDate && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ mb: 1.5, display: "block" }}
                >
                  TIME SLOT
                </Typography>
                <TimeRangeSelector
                  startTime={coverageForm.startTime}
                  endTime={coverageForm.endTime}
                  onStartTimeChange={(value) =>
                    setShiftForm({ ...coverageForm, startTime: value })
                  }
                  onEndTimeChange={(value) =>
                    setShiftForm({ ...coverageForm, endTime: value })
                  }
                  minTime={
                    selectedShop.defaultHours[getDayOfWeek(selectedDate)]
                      .openTime
                  }
                  maxTime={
                    selectedShop.defaultHours[getDayOfWeek(selectedDate)]
                      .closeTime
                  }
                  step={30}
                  format="24h"
                  showSlider={false}
                  showPresets={false}
                />
              </Box>
            )}

            <Divider />

            <Box>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5, display: "block" }}
              >
                STAFF REQUIREMENT
              </Typography>
              <TextField
                label="Minimum Coverage"
                type="number"
                value={coverageForm.minCoverage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setShiftForm({
                    ...coverageForm,
                    minCoverage: parseInt(e.target.value) || 1,
                  })
                }
                fullWidth
                inputProps={{ min: 1 }}
                helperText="Minimum number of staff required during this time"
              />
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setShiftDialogOpen(false)} size="large">
            Cancel
          </Button>
          <Button
            onClick={handleSaveShift}
            variant="contained"
            size="large"
            sx={{ minWidth: 100 }}
          >
            {selectedCoverage ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Shifts Cell Component
function CoverageCell({
  date,
  shop,
  dayHours,
  coverage,
  isUsingDefault,
  isExplicitlyClosed,
  canWrite,
  canAddMore,
  onEdit,
  onDelete,
  onAddShift,
  onSetDefault,
  onRevertToDefault,
  onCopyDay,
  onPasteDay,
  hasCopiedData,
  errors,
  hasChanges,
  validationErrors,
}: {
  date: Date;
  shop: Shop;
  dayHours: { isOpen: boolean; openTime: string; closeTime: string };
  coverage: CoverageRequirement[];
  isUsingDefault: boolean;
  isExplicitlyClosed: boolean;
  canWrite: boolean;
  canAddMore: boolean;
  onEdit: (
    shop: Shop,
    date: Date,
    coverage: CoverageRequirement,
    isDefault: boolean
  ) => void;
  onDelete: (
    shop: Shop,
    date: Date,
    coverage: CoverageRequirement,
    isDefault: boolean
  ) => void;
  onAddShift: (shop: Shop, date: Date) => void;
  onSetDefault: (shop: Shop, date: Date) => void;
  onRevertToDefault: () => void;
  onCopyDay: (shop: Shop, date: Date) => void;
  onPasteDay: (shop: Shop, date: Date) => void;
  hasCopiedData: boolean;
  errors: string[];
  hasChanges: boolean;
  validationErrors: Record<string, string>;
}) {
  // Helper to get error for a specific coverage item
  const getCoverageError = (coverageId: string): string | null => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateKey = `${year}-${month}-${day}`;
    const errorKey = `${shop.id}_${dateKey}_${coverageId}`;
    return validationErrors[errorKey] || null;
  };

  // Determine cell styling based on state
  const getCellBgColor = () => {
    if (!dayHours.isOpen) return "grey.100";
    if (errors.length > 0) return "error.50";
    if (hasChanges) return "warning.50";
    return "background.paper";
  };

  const getCellBorder = () => {
    if (errors.length > 0) return "2px solid";
    if (hasChanges) return "2px solid";
    return undefined;
  };

  const getCellBorderColor = () => {
    if (errors.length > 0) return "error.main";
    if (hasChanges) return "warning.main";
    return undefined;
  };

  return (
    <TableCell
      sx={{
        minWidth: 180,
        bgcolor: getCellBgColor(),
        verticalAlign: "top",
        p: 1,
        border: getCellBorder(),
        borderColor: getCellBorderColor(),
      }}
    >
      {dayHours.isOpen ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {dayHours.openTime} - {dayHours.closeTime}
            </Typography>
            {canWrite && (
              <Box sx={{ display: "flex", gap: 0.25 }}>
                {coverage.length > 0 && (
                  <Tooltip title="Copy day">
                    <IconButton
                      size="small"
                      onClick={() => onCopyDay(shop, date)}
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {hasCopiedData && (
                  <Tooltip title="Paste coverage">
                    <IconButton
                      size="small"
                      onClick={() => onPasteDay(shop, date)}
                      sx={{ p: 0.25 }}
                      color="primary"
                    >
                      <ContentPaste fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
          {coverage.length === 0 ? (
            isExplicitlyClosed ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box
                  sx={{
                    py: 1,
                    px: 1.5,
                    bgcolor: "error.lighter",
                    borderRadius: 1,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="error.main"
                  >
                    No Coverage (Closed)
                  </Typography>
                </Box>
                {canWrite && (
                  <>
                    {canAddMore && (
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => onAddShift(shop, date)}
                        variant="outlined"
                        fullWidth
                      >
                        Add Coverage
                      </Button>
                    )}
                    <Button
                      size="small"
                      onClick={onRevertToDefault}
                      variant="text"
                      color="secondary"
                      fullWidth
                    >
                      Revert to Default
                    </Button>
                  </>
                )}
              </Box>
            ) : (
              canWrite && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Button
                    size="small"
                    onClick={() => onSetDefault(shop, date)}
                    variant="outlined"
                    color="secondary"
                    fullWidth
                  >
                    Use Default
                  </Button>
                  {canAddMore && (
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => onAddShift(shop, date)}
                      variant="outlined"
                      fullWidth
                    >
                      Add Coverage
                    </Button>
                  )}
                </Box>
              )
            )
          ) : (
            <>
              {coverage.map((coverageItem) => (
                <CoverageCard
                  key={coverageItem.id}
                  coverage={coverageItem}
                  isDefault={isUsingDefault}
                  canWrite={canWrite}
                  onEdit={() =>
                    onEdit(shop, date, coverageItem, isUsingDefault)
                  }
                  onDelete={() =>
                    onDelete(shop, date, coverageItem, isUsingDefault)
                  }
                  error={getCoverageError(coverageItem.id)}
                />
              ))}
              {canWrite && (
                <>
                  {canAddMore && (
                    <Button
                      size="small"
                      startIcon={<Add />}
                      onClick={() => onAddShift(shop, date)}
                      sx={{ mt: 0.5 }}
                      variant="outlined"
                      fullWidth
                    >
                      Add Coverage
                    </Button>
                  )}
                  {!isUsingDefault && (
                    <Button
                      size="small"
                      onClick={onRevertToDefault}
                      sx={{ mt: 0.5 }}
                      variant="text"
                      color="secondary"
                      fullWidth
                    >
                      Revert to Default
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      ) : (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ textAlign: "center", display: "block" }}
        >
          Closed
        </Typography>
      )}
    </TableCell>
  );
}

// Coverage Card Component
function CoverageCard({
  coverage,
  isDefault,
  canWrite,
  onEdit,
  onDelete,
  error,
}: {
  coverage: CoverageRequirement;
  isDefault: boolean;
  canWrite: boolean;
  onEdit: () => void;
  onDelete: () => void;
  error: string | null;
}) {
  // Determine styling based on error state
  const getBgColor = () => {
    if (error) return "error.50";
    return isDefault ? "grey.50" : "primary.lighter";
  };

  const getBorderColor = () => {
    if (error) return "error.main";
    return isDefault ? "grey.400" : "primary.main";
  };

  return (
    <Tooltip title={error || ""} placement="top" arrow>
      <Paper
        variant="outlined"
        sx={{
          paddingLeft: 1,
          bgcolor: getBgColor(),
          borderColor: getBorderColor(),
          borderStyle: isDefault && !error ? "dashed" : "solid",
          borderWidth: error ? 2 : 1,
          "&:hover": {
            borderColor: error
              ? "error.dark"
              : isDefault
              ? "grey.600"
              : "primary.dark",
            boxShadow: 1,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              flex: 1,
              py: 0.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {error && <ErrorIcon color="error" sx={{ fontSize: 14 }} />}
              <Typography
                variant="body2"
                fontWeight={600}
                color={error ? "error.main" : "text.primary"}
              >
                {coverage.startTime} - {coverage.endTime}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Min: {coverage.minCoverage} staff
            </Typography>
            {isDefault ? (
              <Chip
                label="Default"
                size="small"
                sx={{
                  height: 16,
                  fontSize: "0.65rem",
                  bgcolor: "grey.300",
                  color: "text.secondary",
                  alignSelf: "flex-start",
                }}
              />
            ) : (
              <Chip
                label="Custom"
                size="small"
                sx={{
                  height: 16,
                  fontSize: "0.65rem",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  alignSelf: "flex-start",
                }}
              />
            )}
          </Box>
          {canWrite && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                color="error"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </Paper>
    </Tooltip>
  );
}
